import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrashIcon } from "lucide-react";
import { useTransformationQueries } from "./useTransformationQueries";

interface ConditionItemProps {
  fieldId: string;
  datapointId: string;
  operator: "AND" | "OR" | "ONLY" | "NOT";
  onDelete: () => void;
  onChange: (fieldId: string, datapointId: string, operator: "AND" | "OR" | "ONLY" | "NOT") => void;
}

export function ConditionItem({ fieldId, datapointId, operator, onDelete, onChange }: ConditionItemProps) {
  const { fields, datapoints } = useTransformationQueries();

  // Get available datapoints for the selected field
  const getAvailableDatapoints = () => {
    if (!fieldId) return [];

    const field = fields?.find((f) => f.id === fieldId);
    if (!field?.primary_dataset) return [];

    return datapoints?.filter((dp) => dp.dataset_id === field.primary_dataset!.id) || [];
  };

  const availableDatapoints = getAvailableDatapoints();

  return (
    <Card className="border border-muted">
      <CardContent className="pt-4 pb-4">
        <div className="grid grid-cols-12 gap-2">
          {/* Field selection */}
          <div className="col-span-4">
            <Select value={fieldId} onValueChange={(newFieldId) => onChange(newFieldId, datapointId, operator)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select field" />
              </SelectTrigger>
              <SelectContent>
                <ScrollArea className="h-72">
                  {fields?.map((field) => (
                    <SelectItem key={field.id} value={field.id}>
                      {field.name}
                    </SelectItem>
                  ))}
                </ScrollArea>
              </SelectContent>
            </Select>
          </div>

          {/* Operator */}
          <div className="col-span-2">
            <Select
              value={operator}
              onValueChange={(value: "AND" | "OR" | "ONLY" | "NOT") => onChange(fieldId, datapointId, value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Operator" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AND">AND</SelectItem>
                <SelectItem value="OR">OR</SelectItem>
                <SelectItem value="ONLY">ONLY</SelectItem>
                <SelectItem value="NOT">NOT</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Datapoint selection */}
          <div className="col-span-5">
            <Select
              value={datapointId}
              onValueChange={(newDatapointId) => onChange(fieldId, newDatapointId, operator)}
              disabled={!fieldId || availableDatapoints.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select value" />
              </SelectTrigger>
              <SelectContent>
                <ScrollArea className="h-72">
                  {availableDatapoints.map((datapoint) => (
                    <SelectItem key={datapoint.id} value={datapoint.id}>
                      {datapoint.name}
                    </SelectItem>
                  ))}
                </ScrollArea>
              </SelectContent>
            </Select>
          </div>

          {/* Delete button */}
          <div className="col-span-1 flex items-center justify-end">
            <Button variant="ghost" size="icon" onClick={onDelete} className="h-8 w-8">
              <TrashIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
